import React, { useEffect } from "react";

import getContentById from "../../../../shared/utils/getContentById";
import _ from "lodash";
import Backgrounds from "../../_partials/Backgrounds/Index";
import TemplateBlock from "../../_partials/TemplateBlock/Index";
import prepareVideoBg from "../../../../shared/functions/prepareVideoBg";
import animationScript, {
  animationCleanUp,
} from "../../../../../animations/worldcup/table";

import assets from "./assets";
import { useTranslation } from "react-i18next";
import Image from "../../../../shared/components/Image";
import ProgressBar from "../../../../shared/components/ProgressBar";
import { getDynamicLogoPath } from "../../../../shared/utils/functions";
import { Trikot } from "../../../assets/svg/Trikot";

require("./style/style.scss");
require("./style/animation.scss");

// Column order of the table. Each value is highlighted green where it is the
// highest of the team and red where it is the lowest.
const STAT_COLUMNS = [
  { key: "goals" },
  { key: "assists" },
  { key: "shots" },
  { key: "ballActions" },
  { key: "passes" },
  { key: "passAccuracy", decimals: 1 },
  { key: "fouls" },
  { key: "duels" },
  { key: "duelsWon", decimals: 1 },
  { key: "yellowCards" },
  { key: "distanceKm", decimals: 1 },
];

const getColumnExtremes = (players) =>
  _.fromPairs(
    STAT_COLUMNS.map(({ key }) => {
      const values = players.map((player) => player[key]).filter(_.isNumber);
      const max = _.max(values);
      const min = _.min(values);
      // A column where everyone has the same value has no best or worst.
      return [key, max === min ? null : { max, min }];
    }),
  );

const Index = (props) => {
  const refs = {
    player: React.createRef(),
    team: React.createRef(),
  };

  const {
    animation,
    data,
    images: Images,
    apiSetup,
    mode,
    format,
    isTimeline,
  } = props;

  const { t, i18n } = useTranslation();

  const id = `${_.get(data, "template_name", "")}_${_.get(data, "uuid", "")}`;

  const prepareAnimation = () => {
    prepareVideoBg({ id });
  };

  useEffect(() => {
    let animationReturn = null;
    if (mode === "animation" || mode === "video") {
      if (animation) {
        prepareAnimation();
        animationReturn = animationScript({
          refs,
          data,
          animation,
          mode,
          id,
          format,
          apiSetup,
          templateName: data?.template_name,
        });
      }
    }

    return function cleanUp() {
      if (animationReturn) {
        animationCleanUp(animationReturn);
      }
    };
  }, [animation, mode]);

  const templateData = _.get(data, "data");

  const { fields, dynamic } = templateData;
  const teamSide = getContentById("selectedSide", fields);

  const matchInfo = _.get(dynamic, "matchInfo");
  const players = _.get(dynamic, "players") || [];

  const teamStatistics = _.castArray(matchInfo?.TeamStatistic || []);
  const homeTeam = _.find(teamStatistics, { TeamRole: "home" }) || teamStatistics[0];
  const awayTeam = _.find(teamStatistics, { TeamRole: "guest" }) || teamStatistics[1];
  const selectedTeam = teamSide === "away" ? awayTeam : homeTeam;

  const backgroundImage = getMainBackground(format, _.get(dynamic, "teamSide"));
  const extremes = getColumnExtremes(players);

  const numberFormat = new Intl.NumberFormat(
    i18n?.language === "de" ? "de-DE" : "en-GB",
    { maximumFractionDigits: 1 },
  );
  const formatValue = (value) => (_.isNumber(value) ? numberFormat.format(value) : "-");

  const getHighlight = (key, value) => {
    const extreme = extremes[key];
    if (!extreme || !_.isNumber(value)) return "";
    if (value === extreme.max) return "best";
    if (value === extreme.min) return "worst";
    return "";
  };

  return (
    <div
      id={id}
      className={`T_${_.get(data, "template_name", "")} ${animation ? "animation" : ""} side-${teamSide}`}
    >
      <TemplateBlock>
        <Backgrounds bg1={backgroundImage} loop={true} />

        <div className={"wc-content wc-content-v2"}>
          <ProgressBar height={"8px"} strokeColor="#177C76" />

          <div className={"dfb-tv-logo"}>
            <img src={assets.sd_dfb_tv_logo} />
          </div>

          <div className={"main-header"}>
            <div className={"header-team-logo"}>
              <Image
                className="team-logo"
                src={getDynamicLogoPath({ team: selectedTeam, feedType: "sportdigital" })}
              />
            </div>
            <span>{t("Title", { keyPrefix: "teamPlayerStats" })}</span>
          </div>

          <div className={"stats-table group-content"}>
            <div className={"table-head"}>
              <div className={"col col-rank"}>#</div>
              <div className={"col col-player"}>
                {t("Player", { keyPrefix: "teamPlayerStats" })}
              </div>
              {STAT_COLUMNS.map(({ key }) => (
                <div key={key} className={`col col-stat col-${key}`}>
                  {t(key, { keyPrefix: "teamPlayerStats" })}
                </div>
              ))}
            </div>

            {players.map((player, index) => (
              <div key={player.PlayerId || index} className={"table-row"}>
                <div className={"col col-rank"}>{index + 1}</div>
                <div className={"col col-player"}>
                  <div className={"player-photo"}>
                    <Image
                      src={`/images/sdPlayers/${player.PlayerId}.webp`}
                      fallback={
                        <div className={"player-photo-fallback"}>
                          <Trikot />
                          <span className={"player-shirt-number"}>
                            {player.shirtNumber}
                          </span>
                        </div>
                      }
                    />
                  </div>
                  <span className={"player-name"}>{player.name}</span>
                  {/* Substitutions: → went off, ← came on. A player can be
                    both (came on, then went off again). Outside .player-name
                    so a long name's ellipsis never cuts the marker off. */}
                  {player.subbedOut && (
                    <span className={"sub-marker sub-out"} title={"Ausgewechselt"}>→</span>
                  )}
                  {player.subbedIn && (
                    <span className={"sub-marker sub-in"} title={"Eingewechselt"}>←</span>
                  )}
                </div>
                {STAT_COLUMNS.map(({ key }) => (
                  <div
                    key={key}
                    className={`col col-stat col-${key} ${getHighlight(key, player[key])}`}
                  >
                    {formatValue(player[key])}
                  </div>
                ))}
              </div>
            ))}
          </div>
        </div>
      </TemplateBlock>
    </div>
  );
};

const getMainBackground = (format, side) => {
  return _.get(assets, `sd_background_1920`);
};

export default Index;
